import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Gauge } from "lucide-react";
import { adminGetUnlockPace, adminSetUnlockPace } from "@/lib/api";
import { Card } from "./kit";

const MILESTONE_IDS = ["7", "30", "100", "365"];
const WEARABLES = [["pin", "🌸 Sakura pin"], ["crown", "👑 Gold crown"], ["hat", "🎂 Birthday hat"]];
const PATH_WORD = { days: "day", tasks: "tasks", rice: "rice balls", streak: "-day streak" };

// "day 30 · 50 tasks · 25 rice balls" (+ birthday for the hat)
function ladderText(paths, id) {
    const parts = Object.entries(paths || {}).map(([p, n]) =>
        p === "days" ? `day ${n}` : p === "streak" ? `${n}${PATH_WORD.streak}` : `${n} ${PATH_WORD[p]}`,
    );
    if (id === "hat") parts.push("your birthday");
    return parts.join(" · ");
}

// Owner switch: Normal ladder vs compressed Beta ladder. Beta only affects
// accounts flagged is_beta; earned unlocks are never taken away on switch.
export default function UnlockPaceCard() {
    const [data, setData] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try { setData(await adminGetUnlockPace()); } catch { /* auth gate */ }
    }, []);
    useEffect(() => { load(); }, [load]);

    const setMode = async (mode) => {
        if (!data || saving || data.mode === mode) return;
        setSaving(true);
        try {
            await adminSetUnlockPace(mode);
            setData({ ...data, mode });
            toast.success(mode === "beta"
                ? "beta pace on 🍡 testers can earn every wearable in ~2 weeks"
                : "normal pace restored 🌸 testers keep what they've earned");
        } catch { toast.error("couldn't switch pace — try again?"); }
        finally { setSaving(false); }
    };

    if (!data) return null;
    const active = data[data.mode];
    const row = (label, normal, beta) => (
        <tr key={label} className="text-xs">
            <td className="py-1 pr-3 text-warm-500">{label}</td>
            <td className={`py-1 pr-3 ${data.mode === "normal" ? "text-warm-900 font-semibold" : "text-warm-500"}`}>{normal}</td>
            <td className={`py-1 ${data.mode === "beta" ? "text-warm-900 font-semibold" : "text-warm-500"}`}>{beta}</td>
        </tr>
    );

    return (
        <Card className="p-6" data-testid="unlock-pace-card">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <span className="text-warm-500 text-xs uppercase tracking-wide font-semibold flex items-center gap-1.5">
                        <Gauge className="w-3.5 h-3.5" /> Unlock pace
                    </span>
                    <p className="text-warm-800 text-sm mt-2 max-w-md">
                        Wearables unlock by <span className="font-semibold">whichever path comes first</span> — days together, tasks finished,
                        rice balls fed, a habit streak, or the user&apos;s birthday (hat). Beta pace halves the numbers for
                        <span className="font-semibold"> beta testers only</span>; everyone else keeps the normal ladder.
                    </p>
                    <p className="text-warm-500 text-xs mt-1" data-testid="unlock-pace-status">
                        active now: <span className="font-semibold text-warm-900">{data.mode === "beta" ? "Beta (fast)" : "Normal"}</span>
                        {" · "}sunglasses after {active.sunglasses} Pomodoros
                    </p>
                </div>
                <div className="flex gap-1.5 bg-surface p-1 rounded-full shrink-0" role="radiogroup" aria-label="Unlock pace">
                    {[{ id: "normal", label: "Normal" }, { id: "beta", label: "Beta (fast)" }].map((o) => (
                        <button
                            key={o.id}
                            onClick={() => setMode(o.id)}
                            disabled={saving}
                            role="radio"
                            aria-checked={data.mode === o.id}
                            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors disabled:opacity-60 ${
                                data.mode === o.id ? "bg-terracotta text-cream" : "text-warm-500 hover:text-warm-900"
                            }`}
                            data-testid={`unlock-pace-${o.id}`}
                        >
                            {o.label}
                        </button>
                    ))}
                </div>
            </div>
            <table className="mt-4 w-full" data-testid="unlock-pace-ladder">
                <thead>
                    <tr className="text-[10px] uppercase tracking-wide text-warm-500">
                        <th className="text-left pr-3 font-semibold">Unlock (any one of)</th>
                        <th className="text-left pr-3 font-semibold">Normal</th>
                        <th className="text-left font-semibold">Beta</th>
                    </tr>
                </thead>
                <tbody>
                    {row("Mochi teases you", `day ${data.normal.stages.warming}`, `day ${data.beta.stages.warming}`)}
                    {row("Loving roasts", `day ${data.normal.stages.friendly}`, `day ${data.beta.stages.friendly}`)}
                    {row("Old friend", `day ${data.normal.stages.old_friend}`, `day ${data.beta.stages.old_friend}`)}
                    {WEARABLES.map(([id, label]) => row(label, ladderText(data.normal.unlocks[id], id), ladderText(data.beta.unlocks[id], id)))}
                    {MILESTONE_IDS.map((d) => row(`${d}-day note`, `day ${data.normal.milestones[d]}`, `day ${data.beta.milestones[d]}`))}
                    {row("🕶️ Sunglasses", `${data.normal.sunglasses} Pomodoros`, `${data.beta.sunglasses} Pomodoros`)}
                </tbody>
            </table>
        </Card>
    );
}
